<template>
  <div :class="{ 'service-visits-calendar-page--fullscreen p-4': fullscreen }">
    <b-card no-body class="card-custom service-visits-calendar" header-tag="header">
      <template v-slot:header>
        <div class="card-title service-visits-calendar__title">
          <h3 class="card-label">
            {{ $t("MENU.ITEM.SERVICE.VISITS") }}
            <i class="mr-2"></i>
            <small>{{ $t("SERVICE_VISITS.CALENDAR") }}</small>
          </h3>
        </div>
        <div class="card-toolbar service-visits-calendar__toolbar">
          <router-link
            v-if="checkIfUserHasPermission(['service-visits-show', 'mechanic'])"
            :to="{
              name: fullscreen
                ? 'fullscreen-list-service-visits'
                : 'list-service-visits'
            }"
            class="btn btn-light-primary font-weight-bolder ml-2 service-visits-calendar__toolbar-button"
          >
            <i class="flaticon2-list"></i>
            {{ $t("LABELS.LIST") }}
          </router-link>
          <router-link
            v-if="checkIfUserHasPermission(['service-visits-show', 'mechanic'])"
            :to="{
              name: fullscreen
                ? 'fullscreen-board-service-visits'
                : 'board-service-visits'
            }"
            class="btn btn-light-primary font-weight-bolder ml-2 service-visits-calendar__toolbar-button"
          >
            <i class="flaticon2-layers-1"></i>
            {{ $t("SERVICE_VISITS.BOARD") }}
          </router-link>
          <router-link
            v-if="!fullscreen && checkIfUserHasPermission(['service-visits-show', 'mechanic'])"
            :to="{ name: 'fullscreen-calendar-service-visits' }"
            target="_blank"
            class="btn btn-light-primary font-weight-bolder ml-2 service-visits-calendar__toolbar-button service-visits-calendar__fullscreen-link"
          >
            <i class="flaticon2-expand"></i>
            {{ $t("BUTTON.FULLSCREEN") }}
          </router-link>
          <router-link
            v-permission="['service-visits-create']"
            :to="{ name: 'add-service-visits' }"
            class="btn btn-light-success font-weight-bolder ml-2 service-visits-calendar__toolbar-button"
          >
            <i class="flaticon2-plus"></i>
            {{ $t("LABELS.ADD_NEW") }}
          </router-link>
        </div>
      </template>

      <v-skeleton-loader v-if="isPageLoading" type="article" />

      <div v-else class="p-5">
        <ServiceVisitFilters
          :filters.sync="filters"
          :mechanics="mechanics"
          :priorities="priorities"
          :service-locations="serviceLocations"
          :statuses="filterStatuses"
          :show-mechanic="canShowServiceVisitMechanicFilter"
          :hide-search-input="true"
          mobile-sheet
          @search="applyServiceVisitFilters"
          @clear="clearServiceVisitFilters"
        />

        <ServiceVisitsCalendarControls
          :selected-month-number="selectedMonthNumber"
          :selected-year="selectedYear"
          :month-options="monthOptions"
          :year-options="yearOptions"
          :selected-month-label="selectedMonthLabel"
          @previous="goToPreviousMonth"
          @next="goToNextMonth"
          @update-month="selectedMonthNumber = $event"
          @update-year="selectedYear = $event"
        />

        <ServiceVisitsCalendarTimeline
          ref="timeline"
          :days="days"
          :bars="calendarBars"
          :calendar-grid-style="calendarGridStyle"
          :calendar-body-style="calendarBodyStyle"
          :body-row-count="bodyRowCount"
          :fullscreen="fullscreen"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { permissionMixin } from "@/core/mixins/permissionMixin.js";
import { serviceVisitBoardDatasetMixin } from "@/modules/service-visits/mixins/serviceVisitBoardDatasetMixin.js";
import { serviceVisitCalendarMixin } from "@/modules/service-visits/mixins/serviceVisitCalendarMixin.js";
import { serviceVisitFilterOptionsMixin } from "@/modules/service-visits/mixins/serviceVisitFilterOptionsMixin.js";
import ServiceVisitFilters from "@/modules/service-visits/components/ServiceVisitFilters.vue";
import ServiceVisitsCalendarControls from "@/modules/service-visits/components/ServiceVisitsCalendarControls.vue";
import ServiceVisitsCalendarTimeline from "@/modules/service-visits/components/ServiceVisitsCalendarTimeline.vue";

export default {
  name: "ServiceVisitsCalendar",
  props: {
    fullscreen: {
      type: Boolean,
      default: false
    }
  },
  mixins: [
    permissionMixin,
    serviceVisitBoardDatasetMixin,
    serviceVisitCalendarMixin,
    serviceVisitFilterOptionsMixin
  ],
  components: {
    ServiceVisitFilters,
    ServiceVisitsCalendarControls,
    ServiceVisitsCalendarTimeline
  },
  watch: {
    selectedMonthLabel() {
      this.scrollToCurrentDate();
    }
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: this.$i18n.t("MENU.ITEM.SERVICE.VISITS"),
        route: { name: "calendar-service-visits" }
      },
      { title: this.$i18n.t("SERVICE_VISITS.CALENDAR") }
    ]);
    this.loadServiceVisitFilterOptions();
    this.getBoard().then(() => this.scrollToCurrentDate());
  },
  methods: {
    applyServiceVisitFilters() {
      this.getBoard();
    },
    clearServiceVisitFilters() {
      this.resetServiceVisitFilters();
      this.getBoard();
    },
    scrollToCurrentDate() {
      this.$nextTick(() => {
        if (this.$refs.timeline) {
          this.$refs.timeline.scrollToCurrentDate();
        }
      });
    }
  }
};
</script>

<style scoped>
.service-visits-calendar ::v-deep .card-body {
  padding: 0;
}

.service-visits-calendar-page--fullscreen {
  min-width: calc(100vw - 2rem);
  width: max-content;
}

.service-visits-calendar-page--fullscreen ::v-deep .card {
  min-width: calc(100vw - 2rem);
  overflow: visible;
  width: max-content;
}

.service-visits-calendar-page--fullscreen ::v-deep .card-body {
  min-width: calc(100vw - 2rem);
  overflow: visible;
  width: max-content;
}

@media (max-width: 767.98px) {
  .service-visits-calendar ::v-deep .card-header {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .service-visits-calendar__title {
    flex: 1 1 100%;
    margin-bottom: 0.75rem;
  }

  .service-visits-calendar__toolbar {
    display: grid;
    flex: 1 1 100%;
    gap: 0.5rem;
    grid-auto-columns: minmax(0, 1fr);
    grid-auto-flow: column;
  }

  .service-visits-calendar__toolbar-button {
    align-items: center;
    display: flex;
    font-size: 0.75rem;
    justify-content: center;
    margin-left: 0 !important;
    min-height: 2.5rem;
    padding: 0.5rem;
    white-space: nowrap;
  }

  .service-visits-calendar__toolbar-button i {
    margin-right: 0.25rem;
  }

  .service-visits-calendar__fullscreen-link {
    display: none;
  }
}
</style>
